import React from 'react';
import BlogArticle from 'components/blog/blogArticle';
import { Post } from 'components/blog/content/styled';
import Link from 'components/ui/link';
import { authorMargo as author } from 'data/authors';
import img from 'img/blog/covers/saas-pricing-ab.png';
import img1 from 'img/blog/how-to-ab-test-saas-pricing-pages-in-2021/netflix.png';
import img2 from 'img/blog/how-to-ab-test-saas-pricing-pages-in-2021/stats.png';
import img3 from 'img/blog/how-to-ab-test-saas-pricing-pages-in-2021/billing.png';
import img4 from 'img/blog/how-to-ab-test-saas-pricing-pages-in-2021/hubspot.png';
import img5 from 'img/blog/how-to-ab-test-saas-pricing-pages-in-2021/treehouse.png';
import img6 from 'img/blog/how-to-ab-test-saas-pricing-pages-in-2021/invoiceocean.png';
import img7 from 'img/blog/how-to-ab-test-saas-pricing-pages-in-2021/proof.png';
import img8 from 'img/blog/how-to-ab-test-saas-pricing-pages-in-2021/user.png';

const Content = () => {
  return (
    <Post>
      <p>
        {' '}
        <Link
          href="https://www.priceintelligently.com/blog/ab-test-pricing-page-strategy"
          rel="noopener noreferrer nofollow"
          target="_blank"
        >
          According to ProfitWell
        </Link>
        , experimenting with changing SaaS pricing can backfire. Users who have paid a higher price
        for their subscription can go mad finding out you have recently reduced the price for your
        software by half.{' '}
      </p>
      <p>
        So is it worth{' '}
        <Link
          href="/tutorials/guide-to-ab-testing-with-livesession/"
          rel="noopener noreferrer"
          target="_blank"
        >
          conducting A/B tests
        </Link>{' '}
        on your pricing page?{' '}
      </p>
      <p>
        Yes, why not give it a try if a higher conversion rate is at stake! However, you should be
        careful.
      </p>
      <p>
        Testing different pricing strategies can be arguable. However, you can definitely test other
        elements of your pricing page content.
      </p>
      <p>
        Companies such as Netflix are often testing their pricing pages attempting to optimize their
        conversion rates. For example, a recent{' '}
        <Link
          href="https://goodui.org/leaks/netflix-keeps-its-older-price-comparison-table-and-rejects-a-new-plan-layout-in-this-a-b-test/"
          rel="noopener noreferrer nofollow"
          target="_blank"
        >
          A/B test of Netflix’s pricing
        </Link>{' '}
        has revealed that a more traditional way of organizing pricing as table comparison (table A)
        performs better than self-contained tables (table B).
      </p>
      <img src={img1} alt="Netflix plans" title="A/B test on Netflix" />
      <p>Source: GoodUI.org</p>
      <p>
        The Netflix case shows that without questioning your assumptions and confronting them with
        users, you won’t be able to improve your conversion rate. That is why give A/B tests a try
        to see how small changes to your pricing table can make a difference on your company’s
        bottom line. In this article, we will explore how to approach A/B tests of pricing pages the
        right way.
      </p>
      <p>
        Let’s start by exploring what elements are worth testing out to improve your pricing page
        performance and, as a result, boost your SaaS sales .
      </p>
      <h2>1. A/B testing pricing pages’ credibility signals </h2>
      <p>
        Having worked in the software development field for a bit, I have noticed that the majority
        of software development agencies treat portfolios as their biggest asset. When everyone is
        selling lines of code in your region, there are not many possible options to stand out
        except for showing what clients you serve and the results you generate. Having sent
        thousands of cold emails to decision-makers one could notice that “big names” and
        recognizable brands in email headlines bring higher conversion. Also, describing results as
        numbers – “20% increase in” or “300% higher revenue” – worked miracles where one could not
        really compete much with more affordable competitors.{' '}
      </p>
      <p>
        When choosing an agency to write code for you (some black magic you don’t really
        understand), trust matters. The same applies to SaaS businesses that operate in highly
        competitive fields – a red ocean.
      </p>
      <p>
        When trying to convince a user into paying for a SaaS subscription, you have to break
        through multiple objections. While it is easier to get a user’s buy-in to register for a
        free trial, having them provide credit card details after trial finishes can be a challenge.
      </p>
      <p>
        Even more friction appears when users are required to provide credit card information before
        starting their free trial. This factor can contribute to a higher bounce rate of your
        pricing or registration pages.
      </p>
      <p>
        However, remember that you can respond to client objections early enough in the buying
        process to reduce friction and boost page conversion rate.
      </p>
      <p>Here are some of the elements that help build trust with users. </p>
      <ul>
        <li>security badges (Norton, McAfee)</li>
        <li>reviews (Capterra, Trustpilot, G2)</li>
        <li>client testimonials</li>
        <li>media mentions</li>
        <li>money back guarantee</li>
      </ul>
      <p>
        In some industries such as healthcare subscription businesses where patient data is
        collected, one should comply with certain requirements such as having a HIPAA-compliant
        website platform.
      </p>
      <p>
        In all these cases, you have to show your trust signals to users in the relevant placement
        on your pricing page.
      </p>
      <p>What trust signals could work the best? </p>
      <p>
        There is no definite answer – you should experiment to find out what works the best for your
        business. However, in some cases, you can rely on the studies or experiments that other
        companies have conducted and made public.
      </p>
      <p>
        For example, Baymard has conducted a study in an attempt to learn how particular security
        badges affect users’ sense of security. Here is what they have discovered.
      </p>
      <img
        src={img2}
        alt="Which badge gives you best sense of trust when paying online? - statistics"
        title="A/B testing pricing pages’ credibility signals"
      />
      <p>Source: Baymard</p>
      <h2>2. Pricing page A/B tests: reorganizing pricing plan</h2>
      <p>
        The order in which subscription plans are placed on the pricing page can significantly
        impact your users’ final purchase decision. While claiming to make rational decisions, your
        users can still be subject to different cognitive biases. Let’s review at least two of them
        and help you use biases to your advantage.
      </p>
      <p>
        <strong>Primacy effect</strong> explains how the order in which the page elements are
        organized affects what product or plan a user chooses.
      </p>
      <p>
        The first thing users see on the pricing page will affect the perception of the following
        elements they are exploring later on. Therefore, you have to set the right expectations in
        the beginning showing a user the right plan.
      </p>
      <p>Which plan should you highlight?</p>
      <p>
        Before answering this question, let’s check the other bias – extremeness aversion for a
        broader context.
      </p>
      <p>
        <strong>“Extremeness aversion”</strong> is all about avoiding one extreme or the other when
        making a decision. In short, users are avoiding the cheapest and most expensive options.
      </p>
      <p>
        Now, let’s check out the example of Toggl pricing to examine what effect both biases can
        have on a user’s final decision.
      </p>
      <img
        src={img3}
        alt="Choose your billing - plans"
        title="Pricing page A/B tests: reorganizing pricing plan"
      />
      <p>
        As you see, the first plan a user sees after entering the page, is the “Best for the teams”
        which significantly stands out from the rest of the plans. Toggl is “forcing” page visitors
        to benchmark against the most expensive plan.
      </p>
      <p>Let’s hypothesize user actions after they see the most expensive plan.</p>
      <p>
        After exploring the most expensive plan, they can start comparing it with the rest and see
        cheaper plans. While a free plan might be too limited in its features, Starter plan can end
        up to be one of the most optimal options, compared to the most expensive plan. This way a
        user chooses an option in the middle.
      </p>
      <p>
        Do you still have a free plan highlighted on your pricing page? Perhaps, this is the time to
        test if your users face primacy effects and if you can harness it to convince them into
        subscribing for a more expensive plan.
      </p>
      <blockquote>
        <p>
          <em>
            The first thing to remember is that A/B testing for your product pricing isn’t a one
            time thing; it’s a continuous project. Secondly, you need to make sure that you’re
            testing your prices with multiple margins. We’re always led to believe that ‘the highest
            the price, the lower the demand’ but, in 2021 this isn’t strictly true - consumers are
            looking for good quality and a brand that they trust so multiple margins are necessary
            for a brand to find the sweet spot.
          </em>
        </p>

        <p>
          <strong>
            Milosz Krasinski, Managing Director at web consulting company{' '}
            <Link
              href="https://chillifruit.com/"
              rel="noopener noreferrer nofollow"
              target="_blank"
            >
              chillifruit.com
            </Link>
          </strong>
        </p>
      </blockquote>
      <h2>3. Introduce urgency or bundle products</h2>
      <p>
        I believe that giving discounts is, as a rule, a bad practice for a SaaS business. Would you
        really appreciate something if given for free right away? Especially if the product’s price
        is comparable to the price of two beers? One of the SaaS businesses I started working with
        used to have promo codes as a way to encourage users to buy a subscription. In fact,
        everyone could have access to it, with no effort. Having analyzed the numbers of how many
        users use a promo code at a checkout, we saw there was just a small fraction of users
        inputting it at the checkout. With this in mind, instead of giving a discount right away,
        one should use them as a reward for a certain action and give it only for a brief period of
        time.
      </p>
      <p>
        I highly recommend testing urgency elements on your pricing page and experiment by showing
        discounts on a pricing page only to those user segments that are close to conversion and
        need a final push, an incentive.
      </p>
      <p>
        For example, you could introduce a time-limited offer or discount and set up a timer for an
        offer expiration.{' '}
        <Link
          href="https://eduardklein.com/best-website-builder/"
          rel="noopener noreferrer dofollow"
          target="_blank"
        >
          Some website builders
        </Link>{' '}
        help you integrate this widget with a few clicks.
      </p>
      <p>
        Just make sure you stick to your words – change prices dynamically after the offer time
        passes.{' '}
      </p>
      <p>
        You can also test out how more flexibility in pricing can contribute to a higher conversion
        rate. For example,{' '}
        <Link
          href="https://www.bluleadz.com/blog/hubspot-pricing"
          rel="noopener noreferrer dofollow"
          target="_blank"
        >
          Hubspot offers an option
        </Link>{' '}
        to create your own offer bundle by adding or removing features.{' '}
      </p>
      <p>
        In more complex SaaS products, there are features users are not utilizing frequently. That
        is why it can be a good idea to get users to choose and pay only for the features that bring
        value. It might or may not apply to your business – just give it a try and set a new page
        variant for A/B tests.
      </p>
      <img src={img4} alt="Hubspot" title="Introduce urgency or bundle products" />
      <h2>4. Perfect your copy</h2>
      <p>
        When rewriting your SaaS pricing page copy, try to focus on benefits for users more than
        explaining product features. Don’t go too much into technical SaaS copywriting – leave this
        language for your Knowledge Base. Consider rewriting some elements of your copy to show
        value for a user – terminology is a secondary thing.
      </p>
      <blockquote>
        <p>
          <em>
            The most high-converting pricing pages have results-driven copy. Instead of emphasizing
            the feature or technology each plan includes, they emphasize the outcome of
            said-feature. When writing any content on the page, ask yourself: “why should my user
            care? what’s in it for them? For example: if one of your features includes 500
            templates, you could mention that alone (as SaaS companies often do) or you could pair
            it with the outcome, and create a more compelling bullet point that drives more
            conversions.
          </em>
        </p>
        <p>
          <em>
            Your job is to simplify your prospect’s decision-making process and remove the friction.
            You do that by explaining exactly what’s in it for them, and why they need this feature.
          </em>
        </p>
        <p>
          <strong>
            Talia Wolf, the Founder of{' '}
            <Link href="https://getuplift.co" rel="noopener noreferrer nofollow" target="_blank">
              GetUplift
            </Link>
          </strong>
        </p>
      </blockquote>
      <p>
        Here is an example of a pricing plan copy where value is communicated. Treehouse is not
        focussing extensively on features, but explains what users can derive from using a product.{' '}
      </p>
      <img src={img5} alt="Treehouse signup page" title="Perfect your copy" />
      <p>
        Consider arranging a copy on a page variant in a different way – look at it with your users’
        eyes. Ask yourself critical questions such as “What benefits does a feature bring and what
        ultimate goal does it help achieve?”.{' '}
      </p>
      <h2>5. Drive better quality traffic by testing blog CTAs and headlines</h2>
      <p>
        Having seen a few CTA experiments on both blog and product pages of enterprise businesses, I
        have noticed that what the whole team thought was a good way to encourage a user to talk to
        a sales rep proved to generate two times lower conversion than what everyone thought would
        be a much less attractive option. Users can often surprise us by taking us out of our
        regular way of thinking. However, while doing these tests, I have noticed a few patterns in
        winning experiments that repeat. Here are some of them you can test for your business.
      </p>
      <p>
        Consider changing a call to action with more generic phrases like “Sign up” to more
        action-driven such as “Start a test”. A slight change in a CTA can bring in more targeted
        traffic to your pricing page that will later convert at a higher rate.
      </p>
      <p>Integrate banners with contextual headlines that relate to a blog post content.</p>
      <p>
        Check out how an invoicing SaaS, InvoiceOcean, is applying changing headlines in their{' '}
        article on the best invoicing software .{' '}
      </p>
      <img
        src={img6}
        alt="InvoiceOcean banner"
        title="Drive better quality traffic by testing blog CTAs and headlines"
      />
      <p>Source: InvoiceOcean</p>
      <p>
        You could test out more standard call to action buttons and headlines and more specific ones
        to see if it drives more engagement and results in a higher conversion rate.
      </p>
      <h2>6. Work on eliminating complexity</h2>
      <p>
        It seems to me that marketers tend to overcomplicate things – writing longer sentences where
        one could say the same with a shorter version, giving users more options to choose from by
        incorporating more than one critical CTAs on the pricing page. Having observed users in
        action interacting with my “more clever” and less complicated page versions, I have noticed
        that{' '}
        <Link
          href="https://www.unboundb2b.com/blog/marketing/saas-trends-to-optimize-product-messaging-pages/"
          rel="noopener noreferrer dofollow"
          target="_blank"
        >
          the more straightforward the message is
        </Link>
        , the better it converts for SaaS businesses (with some small exceptions to the rule).
      </p>
      <p>
        That is I recommend experimenting on making your pricing easier to understand. Normally, a
        user should not spend more than 2 minutes to make a final decision. Listing dozens of
        features increases user’s fatigue.{' '}
      </p>
      <p>
        While getting rid of feature listings is not always a good idea, you can create a page
        variant that helps users navigate through complex pricing. For example, you could ask a user
        questions and organize them in a quiz to narrow down available plan options.
      </p>
      <p>
        If your pricing depends on the frequency of use such as number of contacts or team members,
        integrating a dynamic slider on your pricing page can help users understand the software
        cost faster.
      </p>
      <p>Check out how Proof is using a slider to help users evaluate the price.</p>
      <img src={img7} alt="Proof page" title="Work on eliminating complexity" />
      <p>Source: Proof</p>
      <p>
        Apart from integrating some dynamic elements to help users navigate your offer, you can A/B
        test how animation videos improve time spent on page and conversion.{' '}
      </p>
      <h2>7. Include a FAQ page</h2>
      <p>
        Having worked on B2B SaaS web page copy, I used to design the content based on what the
        business could offer in terms of either a vitamin (nice-to have benefits) or painkiller
        (indispensable features). What I frequently did not account for and that I could use to get
        more sceptical prospects to go through the checkout process was answering their objectives.
        Only by analyzing support tickets and talking to customers more, did I manage to write a
        better converting page copy and answered the biggest objections with a F.A.Q. page.
      </p>
      <p>
        By incorporating an F.A.Q. section beneath the pricing table in your page variant, you can
        test out if this element helps you boost page conversion.
      </p>
      <p>
        Collect the most frequent questions your users have by going through the support tickets or
        recall previous interactions with users you had in the past (for example, usability
        interviews or calls) to collect some ideas for this section.
      </p>
      <p>Check out how User.com is using F.A.Q. section at the bottom of their pricing page.</p>
      <img src={img8} alt="user.com FAQ page" title="Include a FAQ page" />
      <p>Source: User.com</p>
      <h2>How to conduct an A/B test?</h2>
      <p>
        Now, as you found out about different page elements that you can A/B test to generate a
        higher SaaS conversion, let’s check out how you can put this knowledge in action.{' '}
      </p>
      <p>Here is a quick guide on doing A/B tests (if you are doing it for the first time). </p>
      <h3>1. Formulate your A/B test hypothesis and state your goal</h3>
      <p>For example, you can aspire to increase conversion by 10% by changing pricing box copy.</p>
      <p>
        <i>
          Hint: Start with Google Analytics to identify challenges – e.g. high bounce rate,{' '}
          <Link href="/heatmaps/" rel="noopener noreferrer" target="_blank">
            use heatmaps
          </Link>{' '}
          to see where users churn on your pricing page. One of the top mistakes in SaaS marketing
          and price optimization is not asking users about their opinion and viewpoint. If you want
          to get a good starting point for building a hypothesis, ask users about their objections.
        </i>
      </p>
      <h3>2. Create a challenger page</h3>
      <p>
        While a control page is the page you already have – your current pricing page, a challenger
        page or a page variant is the one you will be using for a test. Build a landing page to test
        changes one by one – don’t mix different tests together as in the end you won’t get a
        reliable outcome. By testing one thing at a time, you can achieve spectacular results. For
        example, a CRO agency, SplitBase has launched two separate experiments where they tested a
        modified headline copy. Both versions performed better than the control, with one of them
        bringing a 27% increase in conversion rate.
      </p>
      <h3>3. Use an A/B testing tool</h3>
      <p>
        Choose some tool that will help you conduct the experiment. You can check among free and
        paid tools. There are a number of considerations you need to take into account before you
        buy an A/B testing tool such as your testing needs, the impact on site speed, integrations,
        customer support, and, finally, pricing.
      </p>
      <h3>4. Give it some time</h3>
      <p>
        Don’t rush with conclusions – give your A/B test a time to collect enough user sessions for
        higher significance. Sample size matters a lot when it comes to the test data reliability. A
        predominant number of tests end up to be inconclusive because they end too early.
      </p>
      <h3>5. Interpret results</h3>
      <p>
        With an affluence of data, the world has become data-rich, but insight poor. Analyzing
        results in the right way determines your success with A/B testing and their business value.
        Pay attention to the elements such as percentage increase in your metric and confidence
        levels. This will help you determine a winning variation. Then deploy a winning page (if
        this was your challenger page). If your test results are inconclusive, you can consider
        preparing a summary of the test, draw conclusions and conduct one more test with a new
        hypothesis.
      </p>
      <h2>Things to remember about before you start A/B tests</h2>
      <p>
        Work on the accuracy of your tests. It is not enough to launch a test, wait for some time,
        check the data, and choose a winner. If you want your A/B tests to be trustworthy, you need
        to account for a couple of other important aspects in A/B testing.
      </p>
      <h3>Target the right audience</h3>
      <p>
        Users coming from a social media channel are different from those coming from Google search.
        By understanding behavioural patterns of your users better and running an analysis with the
        data you already have can help avoid sample size pollution.
      </p>
      <h3>Calculate a sample size for your test</h3>
      <p>
        Use available online sample size calculators to crunch numbers faster. If you need more
        sessions to fit into your target sample size, consider driving traffic with paid channels.
        You can consider running dark posts to get more people from your target audience to land on
        your website.
      </p>
      <h3>Remember about statistical significance.</h3>
      Set your statistical significance rate based on your risk appetite. However, remember that the
      acceptable rate for more reliable outcomes is not less than 95%.
      <h3>Supplement findings with user interviews</h3>
      <p>
        A/B tests can help you gain insight into qualitative data. However, you can still ramble
        trying to understand while users choose version A over B. That is why you should also
        consider collecting qualitative feedback. Run a survey or conduct more in-depth interviews
        with users to ask them more questions coming out from the context of your conversation.
      </p>
      <h2>Final words on A/B tests of SaaS pricing pages</h2>
      <p>
        I hope that this article has given you some inspiration on approaching A/B tests of your
        pricing page.{' '}
      </p>
      <p>
        If you are ready to get down to work and start planning your next A/B test, pay more
        attention to the following.
      </p>
      <p>
        Remember to start with a thorough research and use initial findings to build your test
        hypothesis.{' '}
      </p>
      <p>
        A/B tests require time and good planning so don’t be discouraged if you are not able to
        achieve much in a short period of time.{' '}
      </p>
      <p>
        Keep in mind that in order for A/B tests to work, you have to achieve statistical
        significance and collect sufficient numbers of interactions for each page variant.
      </p>
      <p>Good luck with boosting conversion!</p>
    </Post>
  );
};

const Wrapped = BlogArticle(Content);

export const frontmatter = {
  title: 'How to A/B test SaaS pricing pages in 2021',
  url: '/blog/how-to-ab-test-saas-pricing-pages-in-2021/',
  description: `Changing your SaaS pricing is dangerous. Learn how to do A/B testing the right way. Get inspired with case studies and examples.`,
  author,
  img,
  imgSocial: img,
  date: '2021-06-25',
  category: '',
  group: 'Analytics',
  timeToRead: 14,
};

export default () => (
  <Wrapped
    title={frontmatter.title}
    category={frontmatter.category}
    description={frontmatter.description}
    date={frontmatter.date}
    author={frontmatter.author}
    url={frontmatter.url}
    img={frontmatter.img}
    imgSocial={process.env.GATSBY_HOST + frontmatter.img}
    group={frontmatter.group}
    timeToRead={frontmatter.timeToRead}
  />
);
